import { useState, useEffect } from 'react'
import {
  getGoogleAdsConnections,
  getGoogleAdsInsights,
  discoverGoogleAdsAccounts,
  type GoogleAdsConnection,
  type GoogleAdsInsightRow,
} from '@/lib/api/oauth'

interface UseGoogleAdsResult {
  googleAdsConnections: GoogleAdsConnection[]
  googleAdsCampaignsData: any[]
  googleAdsDailyInsights: GoogleAdsInsightRow[]
  isLoadingGoogleAds: boolean
}

export function useGoogleAds(
  propertyId: string | undefined,
  token: string | undefined,
  dateRange: { since: string; until: string } | null,
): UseGoogleAdsResult {
  const [googleAdsConnections, setGoogleAdsConnections] = useState<GoogleAdsConnection[]>([])
  const [googleAdsCampaignsData, setGoogleAdsCampaignsData] = useState<any[]>([])
  const [googleAdsDailyInsights, setGoogleAdsDailyInsights] = useState<GoogleAdsInsightRow[]>([])
  const [isLoadingGoogleAds, setIsLoadingGoogleAds] = useState(false)

  useEffect(() => {
    if (!propertyId || !token || !dateRange) return
    let stale = false

    const fetchGoogleAds = async () => {
      setIsLoadingGoogleAds(true)
      try {
        const connections = await getGoogleAdsConnections(propertyId, token)
        if (stale) return
        setGoogleAdsConnections(Array.isArray(connections) ? connections : [])

        const allAccountData: any[] = []
        const allDaily: GoogleAdsInsightRow[] = []

        await Promise.all(
          (Array.isArray(connections) ? connections : []).map(async (conn) => {
            // Discover only when nothing is stored: the stored list carries the
            // user's account selection.
            let effectiveConn = conn
            if (!conn.customerAccounts?.length) {
              try {
                effectiveConn = await discoverGoogleAdsAccounts(conn.id, token)
              } catch { /* non-critical — proceed with stored accounts */ }
            }

            // Same rule as the server's ads-attribution spend, so the chart and
            // the Spent tile add up: no manager (MCC) accounts, which can't return
            // campaign metrics, and every account when none was ever deselected.
            const allAccounts = effectiveConn.customerAccounts ?? []
            const managerIds = new Set(allAccounts.map((a) => a.managerId).filter(Boolean))
            const hasExplicitSelection = allAccounts.some((a) => typeof a.isActive === 'boolean')
            const accounts = allAccounts.filter((a) =>
              !a.isManager && !managerIds.has(a.id) && (hasExplicitSelection ? a.isActive !== false : true),
            )

            await Promise.all(
              accounts.map(async (acct) => {
                try {
                  const result = await getGoogleAdsInsights(conn.id, acct.id, dateRange.since, dateRange.until, token)
                  const rows: GoogleAdsInsightRow[] = result?.rows ?? []

                  // Aggregate rows by campaign
                  const campaignMap = new Map<string, {
                    id: string; name: string; status: string
                    spend: number; impressions: number; clicks: number; conversions: number
                  }>()
                  rows.forEach((row) => {
                    const existing = campaignMap.get(row.campaignId)
                    if (existing) {
                      existing.spend += Number(row.spend) || 0
                      existing.impressions += Number(row.impressions) || 0
                      existing.clicks += Number(row.clicks) || 0
                      existing.conversions += Number(row.conversions) || 0
                    } else {
                      campaignMap.set(row.campaignId, {
                        id: row.campaignId,
                        name: row.campaignName,
                        status: row.status,
                        spend: Number(row.spend) || 0,
                        impressions: Number(row.impressions) || 0,
                        clicks: Number(row.clicks) || 0,
                        conversions: Number(row.conversions) || 0,
                      })
                    }
                  })

                  allAccountData.push({
                    connectionId: conn.id,
                    accountId: acct.id,
                    accountName: acct.name,
                    campaigns: Array.from(campaignMap.values()),
                  })
                  allDaily.push(...rows)
                } catch { /* non-critical per-account failure */ }
              })
            )
          })
        )

        if (stale) return
        setGoogleAdsCampaignsData(allAccountData)
        setGoogleAdsDailyInsights(allDaily)
      } catch { /* non-critical */ }
      finally { if (!stale) setIsLoadingGoogleAds(false) }
    }
    fetchGoogleAds()

    return () => { stale = true }
  }, [propertyId, token, dateRange?.since, dateRange?.until])

  return { googleAdsConnections, googleAdsCampaignsData, googleAdsDailyInsights, isLoadingGoogleAds }
}
